import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

// Loads a Radar article by slug, legacy id or old slug, then replaces the URL
// with the canonical slug path (the edge Worker does the same with a real 301).
export const useArticle = (endpoint, key) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(false);
  const current = useRef(null);

  useEffect(() => {
    const cached = current.current;
    if (cached && (cached.slug === key || cached.id === key)) return undefined;
    let alive = true;
    setItem(null);
    setErr(false);
    api.get(`/${endpoint}/${encodeURIComponent(key)}`)
      .then((r) => {
        if (!alive) return;
        current.current = r.data;
        setItem(r.data);
        const canonical = r.data.canonical_path;
        if (canonical && canonical !== location.pathname) {
          navigate(`${canonical}${location.search}${location.hash}`, { replace: true });
        }
      })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, key]);

  return { item, err };
};

// SeoHead props for a single-language article: bare canonical + real hreflang pairs.
export const articleSeoProps = (item) => {
  const canonical = item.canonical_path;
  const alternates = { [item.language || "en"]: canonical };
  if (item.translation) alternates[item.translation.language] = item.translation.path;
  alternates.xDefault = alternates.en || canonical;
  return { path: canonical, canonicalPath: canonical, alternates };
};

// Lets the header language switch jump to the translated record.
export const useArticleLangRoutes = (item) => {
  const { setLangRoutes } = useLang();
  const routes = item
    ? { [item.language || "en"]: item.canonical_path, ...(item.translation ? { [item.translation.language]: item.translation.path } : {}) }
    : null;
  const key = routes ? JSON.stringify(routes) : "";
  useEffect(() => {
    if (!key) return undefined;
    setLangRoutes(JSON.parse(key));
    return () => setLangRoutes(null);
  }, [key, setLangRoutes]);
};
